import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO.jsx';
import Biores2Image from '../../components/assets/Staff/Biores 2.jpg';
import Biores1Image from '../../components/assets/Staff/Biores 1.jpg';
import BICOMVideo from '../../components/assets/Staff/BICOM.mp4';
import FranzMorellImage from '../../components/assets/Staff/OIP (2)a.jpeg';
import { FaHeart, FaBrain, FaLeaf, FaClock, FaUsers, FaShieldAlt, FaExclamationTriangle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const BioresonanceTherapy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const howItWorksRef = useRef(null);
  const originsRef = useRef(null);
  const evidenceRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'how-it-works': howItWorksRef,
      'origins': originsRef,
      'evidence': evidenceRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      const elementTop = targetRef.current.offsetTop;
      const offsetPosition = elementTop - 150; // Account for navbar + sticky nav buttons
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'how-it-works', ref: howItWorksRef },
        { id: 'origins', ref: originsRef },
        { id: 'evidence', ref: evidenceRef }
      ];

      const scrollPosition = window.scrollY + 550; // Increased offset for navbar + sticky nav buttons + extra buffer

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'how-it-works', label: 'How It Works', icon: FaBrain },
    { id: 'origins', label: 'Background', icon: FaLeaf },
    { id: 'evidence', label: 'Evidence & Considerations', icon: FaExclamationTriangle }
  ];

  const resourceLinks = [
    { name: "Healthline – balanced overview of bioresonance and current research context", url: "https://www.healthline.com/health/bioresonance#research" },
    { name: "The Open Epidemiology Journal (2020) – overview of complementary bioresonance research", url: "https://benthamopen.com/FULLTEXT/TOEPIJ-8-1" },
    { name: "UniCamillus – Saint Camillus International University of Health Sciences, Rome – Master's Programme in Bioresonance Methodologies", url: "https://www.unicamillus.org/en/master-bioresonance-methodologies/" }
  ];

  const subTreatments = [
    { id: 'allergies', name: 'Allergies', path: '/therapy/bioresonance-therapy/allergies', description: 'Comprehensive bioresonance treatment for various allergy conditions and sensitivities.' },
    { id: 'addictions', name: 'Addictions', path: '/therapy/bioresonance-therapy/addictions', description: 'Specialized bioresonance therapy to support addiction recovery and detoxification.' },
    { id: 'depression', name: 'Depression', path: '/therapy/bioresonance-therapy/depression', description: 'Holistic bioresonance approach to support mental health and emotional wellbeing.' },
    { id: 'skin-problems', name: 'Skin Problems', path: '/therapy/bioresonance-therapy/skin-problems', description: 'Targeted bioresonance treatment for various skin conditions and dermatological issues.' },
    { id: 'lyme-disease', name: 'Lyme Disease', path: '/therapy/bioresonance-therapy/lyme-disease', description: 'Specialized bioresonance therapy for Lyme disease and related tick-borne illnesses.' },
    { id: 'arthritis', name: 'Arthritis', path: '/therapy/bioresonance-therapy/arthritis', description: 'Comprehensive bioresonance treatment for arthritis and joint inflammation.' },
    { id: 'muscle-joint-spine', name: 'Muscle, Joint and Spine Pain', path: '/therapy/bioresonance-therapy/muscle-joint-spine', description: 'Targeted bioresonance therapy for musculoskeletal pain and spinal conditions.' }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance Therapy" description="BICOM bioresonance therapy at Bioresonance Leicester – gentle, complementary support for balance and wellbeing. Sub-therapies for allergies, stress and more." path="/therapy/bioresonance-therapy" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Therapy</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Bioresonance Therapy</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaBrain className="mr-2" />
                Complementary Wellness
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Bioresonance
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                A complementary, non-invasive, frequency-based approach to supporting balance and wellbeing
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Many people today don't feel "ill" in the medical sense — yet they don't feel truly well either.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Persistent fatigue, recurring sensitivities, constant tension, difficulty relaxing, or the feeling that the body is constantly reacting to something — even when medical tests come back as normal.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                For many individuals, this is the moment when they begin exploring complementary, non-pharmacological approaches that focus not on diagnosing disease, but on supporting balance and the body's natural regulatory processes.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such frequency-based approach.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                  {/* Main image container */}
                  <div className="relative p-4">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-96">
                      <img
                        src={Biores2Image}
                        alt="Bioresonance Therapy"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                    </div>
                    
                    {/* Creative border */}
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                    <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                  </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What is bioresonance?
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed mb-12">
              <p>
                Bioresonance is a gentle, non-invasive method used within the field of complementary wellness. In practice, it is applied as a frequency-based method, built on the observation that the human body produces subtle electromagnetic signals and that internal or external stressors may influence how these signals interact.
              </p>
              <p>
                Rather than aiming to treat or cure medical conditions, sessions using this frequency-based approach are designed to support overall balance, reduce stress load, and encourage the body's own self-regulation mechanisms. The method does not involve medication, invasive procedures or physical manipulation.
              </p>
              <p>
                People often seek this form of support when they feel "out of balance" without a clear medical explanation, experience recurring sensitivities or discomfort, wish to support wellbeing without introducing substances, or are looking for a personalised, holistic approach alongside conventional medical care.
              </p>
              <p>
                Responses vary from person to person and depend on many individual factors.
              </p>
            </div>

            {/* Specialized Bioresonance Treatments */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Specialized Bioresonance Treatments
              </h3>
              <p className="text-gray-600">
                We offer targeted bioresonance therapy for specific conditions and health concerns
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subTreatments.map((treatment, index) => (
                <motion.div
                  key={treatment.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-grey/20"
                >
                  <Link to={treatment.path} className="block group">
                    <h4 className="text-lg font-semibold text-dark-grey mb-3 group-hover:text-gold transition-colors duration-300">
                      {treatment.name}
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">
                      {treatment.description}
                    </p>
                    <div className="flex items-center text-gold group-hover:text-gold/80 transition-colors duration-300">
                      <span className="text-sm font-medium">Learn More</span>
                      <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  How does this frequency-based approach work?
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Every cell in the human body emits electromagnetic signals. When the body is exposed to stress — whether environmental, emotional or lifestyle-related — these signals may become less coherent.
                  </p>
                  <p>
                    During a session, the BICOM® system analyses these signal patterns and reflects them back in a modified form. The intention is not to force change, but to provide the body with information that may support its natural regulatory processes.
                  </p>
                  <p>
                    Many individuals describe sessions as deeply relaxing. Others report subtle changes that develop gradually over time. As with all complementary approaches, experiences and outcomes differ between individuals.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue/20 to-gold/20 h-80">
                    <video
                      src={BICOMVideo}
                      className="w-full h-full object-contain"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      aria-label="Bioresonance – BICOM Technology"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2 group-hover:rotate-0 transition-transform duration-500"></div>
                  {/* Floating elements */}
                  <div className="absolute top-4 right-6 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-4 left-6 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4 flex items-center">
                  <p className="text-gray-600 leading-relaxed">
                    The BICOM® system is used to support the body's natural regulatory processes in a gentle, non-invasive way.
                  </p>
                </div>

                <div className="relative">
                  <div className="relative group">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-96">
                      <img
                        src={Biores1Image}
                        alt="Bioresonance – Frequency Analysis"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2 group-hover:rotate-0 transition-transform duration-500"></div>
                    {/* Floating elements */}
                    <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                    <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Background and development Section */}
      <section ref={originsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaLeaf className="mr-2" />
                Background
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Background and development
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Bioresonance methodology was developed in Germany over 40 years ago and has since been used internationally within complementary health settings. Interest in this frequency-based method has continued to grow, particularly among individuals seeking non-invasive and holistic ways to support wellbeing.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  In recent years, bioresonance has also entered academic discussion. UniCamillus – Saint Camillus International University of Health Sciences in Rome offers a Master's programme in Bioresonance Methodologies, reflecting growing academic interest in this field.
                </p>
              </div>
              
              <div className="relative">
                <div className="relative mx-auto max-w-sm group">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-96">
                    <img
                      src={FranzMorellImage}
                      alt="Franz Morell - Bioresonance Pioneer"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                    {/* Name caption */}
                    <div className="absolute bottom-3 left-3 bg-black/50 text-white text-sm font-semibold px-3 py-1 rounded-full">
                      Franz Morell
                    </div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2 group-hover:rotate-0 transition-transform duration-500"></div>
                  {/* Floating elements */}
                  <div className="absolute top-4 right-6 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-4 left-6 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific context, Legal, Important considerations, In summary, Evidence */}
      <section ref={evidenceRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaExclamationTriangle className="mr-2" />
                Evidence & Considerations
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Scientific context and ongoing discussion
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Bioresonance remains a subject of ongoing discussion within scientific and medical communities. While observational studies, practitioner reports and case analyses describe potential benefits, the overall body of evidence is considered inconclusive by mainstream medicine.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Frequently cited limitations include small sample sizes, lack of standardised protocols and variability in study design. For this reason, bioresonance is generally classified as a complementary approach, rather than a medical treatment.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Individuals considering this frequency-based method are encouraged to explore available scientific literature, remain informed, and approach it with realistic expectations.
                </p>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-80">
                    <video
                      src="/BICOM1.mp4"
                      className="w-full h-full object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      preload="auto"
                      aria-label="BICOM bioresonance device"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2 group-hover:rotate-0 transition-transform duration-500 pointer-events-none"></div>
                  {/* Floating elements */}
                  <div className="absolute top-4 right-6 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-4 left-6 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </div>
            </div>

            <div className="space-y-8 mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  Legal and professional context
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  In Germany, the use of the BICOM® bioresonance device in allergy-related applications was addressed in a legal ruling issued by the Munich Higher Regional Court on 14 May 2009 (case number 6U 2187/06). The ruling confirmed the legitimacy of its use within complementary practice, without classifying it as a medical treatment.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  Important considerations
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  This frequency-based approach does not diagnose medical conditions, does not treat disease and does not replace medical consultation or prescribed medication. Individuals undergoing medical treatment should consult their healthcare provider before starting any complementary method. Responses vary significantly between individuals.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  This approach is best understood as supportive and complementary, used alongside conventional healthcare where appropriate.
                </p>
              </div>

              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  In summary
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Bioresonance, applied as a frequency-based method using the BICOM® system, offers a non-invasive and personalised approach for individuals seeking to support balance and wellbeing in a holistic way.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  Evidence, research and further reading
                </h3>
                <ul className="space-y-3">
                  {resourceLinks.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-gold/80 underline transition-colors"
                      >
                        {link.name}
                      </a>
                      <br />
                      <span className="text-sm text-gray-500 break-all">{link.url}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Bioresonance Therapy?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Discover how bioresonance therapy can support your body's natural healing processes as part of a comprehensive wellness approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default BioresonanceTherapy;
